/**
 * Realtime Ticket Context – native WebSocket (ws).
 * Connects to /realtime-tickets on login for ticket updates (create, update, delete, comment).
 * Protocol: server sends JSON { type, payload }. No Socket.io dependency.
 */

import React, { createContext, useContext, useEffect, useState, useRef, useCallback } from 'react';
import { useAuth } from './AuthContext';

interface RealtimeTicketContextType {
  isConnected: boolean;
  on: (event: string, handler: (data: any) => void) => () => void;
}

const RealtimeTicketContext = createContext<RealtimeTicketContextType>({
  isConnected: false,
  on: () => () => {},
});

export const useRealtimeTicket = () => {
  const context = useContext(RealtimeTicketContext);
  if (!context) {
    throw new Error('useRealtimeTicket must be used within RealtimeTicketProvider');
  }
  return context;
};

interface RealtimeTicketProviderProps {
  children: React.ReactNode;
}

const RECONNECT_DELAY_MS = 3000;
const MAX_RECONNECT_ATTEMPTS = 10;

export const RealtimeTicketProvider: React.FC<RealtimeTicketProviderProps> = ({ children }) => {
  const { user } = useAuth();
  const [isConnected, setIsConnected] = useState(false);
  const wsRef = useRef<WebSocket | null>(null);
  const listenersRef = useRef<Map<string, Set<(data: any) => void>>>(new Map());
  const reconnectAttemptsRef = useRef(0);
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const getWsUrl = useCallback(() => {
    const token = localStorage.getItem('token');
    if (!token) return null;
    const isProduction = window.location.hostname === 'vitreous.support'
      || window.location.hostname.includes('vitreous.support')
      || window.location.hostname === 'patelhrms.com'
      || window.location.hostname.includes('patelhrms.com');
    const protocol = typeof window !== 'undefined' && window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const host = isProduction ? window.location.host : 'localhost:5000';
    return `${protocol}//${host}/realtime-tickets?token=${encodeURIComponent(token)}`;
  }, []);

  const connect = useCallback(() => {
    if (!user) return;

    const url = getWsUrl();
    if (!url) return;

    if (wsRef.current?.readyState === WebSocket.OPEN) return;

    try {
      const ws = new WebSocket(url);

      ws.onopen = () => {
        setIsConnected(true);
        reconnectAttemptsRef.current = 0;
      };

      ws.onmessage = (event) => {
        try {
          const msg = JSON.parse(event.data);
          const { type, payload } = msg;
          const handlers = listenersRef.current.get(type);
          if (handlers) {
            handlers.forEach((fn) => fn(payload));
          }
        } catch {
          // ignore parse errors
        }
      };

      ws.onclose = () => {
        setIsConnected(false);
        wsRef.current = null;
        if (!user) return;
        if (reconnectAttemptsRef.current < MAX_RECONNECT_ATTEMPTS) {
          reconnectAttemptsRef.current += 1;
          reconnectTimerRef.current = setTimeout(connect, RECONNECT_DELAY_MS);
        }
      };

      ws.onerror = () => {
        setIsConnected(false);
      };

      wsRef.current = ws;
    } catch (e) {
      setIsConnected(false);
      if (reconnectAttemptsRef.current < MAX_RECONNECT_ATTEMPTS) {
        reconnectAttemptsRef.current += 1;
        reconnectTimerRef.current = setTimeout(connect, RECONNECT_DELAY_MS);
      }
    }
  }, [user, getWsUrl]);

  const disconnect = useCallback(() => {
    if (reconnectTimerRef.current) {
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
    reconnectAttemptsRef.current = MAX_RECONNECT_ATTEMPTS;
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
    setIsConnected(false);
  }, []);

  const on = useCallback((event: string, handler: (data: any) => void) => {
    if (!listenersRef.current.has(event)) {
      listenersRef.current.set(event, new Set());
    }
    listenersRef.current.get(event)!.add(handler);

    return () => {
      listenersRef.current.get(event)?.delete(handler);
    };
  }, []);

  useEffect(() => {
    if (user) connect();
    else disconnect();
    return () => disconnect();
  }, [user, connect, disconnect]);

  const value: RealtimeTicketContextType = {
    isConnected,
    on,
  };

  return (
    <RealtimeTicketContext.Provider value={value}>
      {children}
    </RealtimeTicketContext.Provider>
  );
};
